import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppNotification } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/components/icons/Icon';
import { useAlerts } from '@/lib/useAlerts';
import { setSoundMuted, soundMuted } from '@/lib/chime';

/**
 * The bell.
 *
 * ── WHY THERE WAS NOT ONE UNTIL NOW ─────────────────────────────────────────
 *
 * There has been a `notifications` table since the trust tables went in, and
 * the refund flow has been writing to it all along — an adverse-reaction report
 * already tells the buyer a clinician will read it within 48 hours. There was
 * simply nowhere to read them: no endpoint, no bell, no screen. Every one of
 * those messages was written into the dark.
 *
 * ── THE RULE THE DOT FOLLOWS ────────────────────────────────────────────────
 *
 * It appears only when there is genuinely something unread. A permanent red dot
 * is a dot people stop seeing, and the one thing this control has to be able to
 * do is interrupt somebody on the day an order they are waiting for is
 * cancelled.
 *
 * ── AND WHY IT POLLS RATHER THAN PUSHES ─────────────────────────────────────
 *
 * No websocket. A persistent connection on a Nigerian mobile network is a
 * battery cost and a reconnect storm, and the panel refetches when opened, so
 * anything that matters is current the moment somebody looks.
 *
 * ── WHY IT IS NO LONGER A MINUTE ────────────────────────────────────────────
 *
 * It was 60s, on the reasoning that a minute of latency on "your order was
 * accepted" costs nobody anything. That is true of a buyer and false of a
 * pharmacy: a minute is a long time to leave a new order sitting unseen on a
 * counter, and the same component draws the bell in the seller console and the
 * admin console.
 *
 * Twenty seconds, plus an immediate refetch whenever the window is focused —
 * which is what actually happens when somebody comes back to the tab, and
 * costs nothing while they are away.
 *
 * ── AND WHY IT MAKES A SOUND ────────────────────────────────────────────────
 *
 * A badge nobody is looking at is not a notification. The chime fires only
 * when the count goes UP and never on a first reading, so arriving at a screen
 * with nine unread is silent and the tenth is audible. See useAlerts.
 */
export function NotificationBell({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: ({ signal }) => api.unreadNotificationCount(signal),
    refetchInterval: 20_000,
    // Not in a background tab: nobody is looking, and a phone that has been in
    // somebody's pocket for an hour should not have spent it polling.
    refetchIntervalInBackground: false,
    // The moment they come back, though.
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  const unread = data ?? 0;

  // Sound on an increase, and the count in the tab title for anybody whose
  // volume is down or whose tab is behind three others.
  useAlerts(unread);

  // Close on a click anywhere else, which is what every menu on every phone
  // does and what people try first.
  useEffect(() => {
    if (!open) return;

    const away = (event: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener('mousedown', away);

    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  return (
    <div ref={wrap} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        data-testid="notification-bell"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        className="relative grid h-11 w-11 shrink-0 place-items-center rounded-[9px] text-muted hover:bg-paper hover:text-ink"
      >
        <Icon name="bell" size={18} strokeWidth={1.7} />

        {unread > 0 ? (
          <span
            data-testid="notification-dot"
            className="absolute right-[3px] top-[3px] grid h-[15px] min-w-[15px] place-items-center rounded-full border-2 border-white bg-danger px-[3px] font-mono text-[9px] font-bold leading-none text-white"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? <Panel onClose={() => setOpen(false)} /> : null}
    </div>
  );
}

function Panel({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: ({ signal }) => api.notifications(false, signal),
    // Opened deliberately, so it is worth a fresh read.
    refetchOnMount: 'always',
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const readAll = useMutation({
    mutationFn: () => api.readAllNotifications(),
    onSuccess: refresh,
  });

  const readOne = useMutation({
    mutationFn: (id: number) => api.readNotification(id),
    onSuccess: refresh,
  });

  const items = data?.notifications ?? [];
  const unread = data?.unread ?? 0;

  // Read once per open rather than held in state: the panel is short-lived,
  // and this way two tabs cannot disagree about it.
  const [muted, setMuted] = useState(soundMuted());

  return (
    <div
      data-testid="notification-panel"
      // ── WHY THIS IS PINNED TO THE VIEWPORT ON A PHONE ────────────────────
      //
      // It used to be `absolute right-0`, anchored to the bell. That is only
      // correct when the bell is the rightmost control in its bar — and on the
      // buyer's app bar it is not, the cart is. So the panel's right edge sat
      // 44px in from the screen, a 340px card then ran off the LEFT edge, and
      // the first line of every notification was cut in half.
      //
      // Below `sm` it is therefore fixed to the viewport with the same 16px
      // gutter the rest of the app uses, so it cannot be clipped whatever sits
      // beside the bell. From `sm` up there is room to hang it off the bell
      // again, which keeps it visually attached to the control that opened it.
      className="fixed inset-x-4 top-[72px] z-40 overflow-hidden rounded-card border border-line bg-white shadow-md sm:absolute sm:inset-x-auto sm:right-0 sm:top-[46px] sm:w-[340px]"
    >
      <div className="flex items-center justify-between gap-2 border-b border-line px-3.5 py-2.5">
        <p className="m-0 text-[13px] font-bold text-ink">Notifications</p>

        {/* ── THE MUTE, WHERE SOMEBODY ANNOYED BY THE SOUND WILL LOOK ──────
            An agent works this desk all day. A sound they cannot find a
            switch for is a sound they solve by turning off the speakers,
            which also loses them the one alert that mattered. */}
        <button
          type="button"
          data-testid="sound-toggle"
          aria-pressed={!muted}
          aria-label={muted ? 'Turn notification sound on' : 'Turn notification sound off'}
          onClick={() => {
            setSoundMuted(!muted);
            setMuted(!muted);
          }}
          className={cn(
            'ml-auto grid h-9 w-9 shrink-0 place-items-center rounded-[8px]',
            muted ? 'text-faint hover:bg-paper' : 'text-blue hover:bg-blue-tint-2',
          )}
        >
          <Icon name={muted ? 'alert' : 'bell'} size={15} strokeWidth={1.9} />
        </button>

        {unread > 0 ? (
          <button
            type="button"
            disabled={readAll.isPending}
            onClick={() => readAll.mutate()}
            className="min-h-9 bg-transparent px-1 text-[12px] font-semibold text-blue"
          >
            {readAll.isPending ? 'Clearing…' : 'Mark all read'}
          </button>
        ) : null}
      </div>

      <div className="max-h-[min(420px,60vh)] overflow-y-auto">
        {isLoading ? (
          <p className="m-0 px-3.5 py-6 text-center text-[13px] text-muted">Loading…</p>
        ) : items.length === 0 ? (
          <div className="px-3.5 py-7 text-center">
            <p className="m-0 text-[13px] font-medium text-ink">Nothing yet</p>
            <p className="m-0 mt-1 text-[12px] leading-snug text-muted">
              Updates about your orders and prescriptions appear here.
            </p>
          </div>
        ) : (
          items.map((one) => (
            <Row
              key={one.id}
              notification={one}
              onOpen={() => {
                if (one.is_unread) readOne.mutate(one.id);
                onClose();
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}

function Row({
  notification,
  onOpen,
}: {
  notification: AppNotification;
  onOpen: () => void;
}) {
  const body = (
    <>
      <span
        className={cn(
          'mt-[3px] grid h-7 w-7 shrink-0 place-items-center rounded-full',
          notification.is_unread ? 'bg-blue-tint text-blue' : 'bg-paper text-faint',
        )}
      >
        <Icon name={iconFor(notification.type)} size={14} strokeWidth={1.9} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold leading-snug text-ink">
          {notification.title}
        </span>
        {notification.body ? (
          <span className="mt-0.5 block text-[12px] leading-snug text-muted">
            {notification.body}
          </span>
        ) : null}
        <span className="mt-1 block text-[11px] text-faint">{ago(notification.created_at)}</span>
      </span>

      {notification.is_unread ? (
        <span aria-hidden className="mt-2 h-[7px] w-[7px] shrink-0 rounded-full bg-blue" />
      ) : null}
    </>
  );

  const className = cn(
    'flex w-full min-h-11 items-start gap-2.5 border-b border-line-2 px-3.5 py-3 text-left no-underline last:border-b-0',
    notification.is_unread ? 'bg-white' : 'bg-white/60',
  );

  // A notification with nowhere to go is still worth marking read, so it is a
  // button rather than a dead link.
  return notification.url ? (
    <Link to={notification.url} onClick={onOpen} className={className}>
      {body}
    </Link>
  ) : (
    <button type="button" onClick={onOpen} className={className}>
      {body}
    </button>
  );
}

/**
 * The icon is keyed off `type`, which is a contract — the title can be reworded
 * by anybody at any time, and an icon chosen from the words would follow it.
 */
function iconFor(type: string): IconName {
  if (type.startsWith('quote')) return 'doc';
  if (type.startsWith('refund')) return 'money';
  if (type.startsWith('licence')) return 'shield';
  if (type.startsWith('pharmacy')) return 'store';
  if (type.includes('dispatched') || type.includes('delivered')) return 'truck';

  return 'orders';
}

/** Short and human. Nobody wants a timestamp on a notification. */
function ago(iso: string | null): string {
  if (!iso) return '';

  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604_800) return `${Math.floor(seconds / 86_400)}d ago`;

  return new Date(iso).toLocaleDateString('en-NG', { day: 'numeric', month: 'short' });
}
